// components/BroadcastList.js
import React from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  IconButton,
  Button,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

// Import permission utilities
import {
  canViewContactNumber,
  canRemarkTicket,
  canUpdateCenter,
  canRefundTicketAmount,
} from "../../../../utility/permissionUtils";

const BroadcastList = ({
  broadCastList,
  ticketDetails,
  handleWhatsAppClick,
  handleOpenRemarkModal,
  handleEditCenter,
  handleOpenRefundModal,
  userPermissions,
}) => {
  // Permission checks
  const canViewNumbers = canViewContactNumber(userPermissions);
  const canAddRemark = canRemarkTicket(userPermissions);
  const canEditCenter = canUpdateCenter(userPermissions);
  const canRefund = canRefundTicketAmount(userPermissions);
  const totalColumns =
    11 + (canViewNumbers ? 1 : 0) + (canAddRemark ? 1 : 0) + (canEditCenter ? 1 : 0) + (canRefund ? 1 : 0);

  const getAttemptDetails = (item) => {
    // API can return `technician_attempts` as either:
    // 1) an array of attempts (each with technician_id, attempted, attemptedAt, ...)
    // 2) a single object (attempted / attemptedAt only) inside each broadcast item
    const attemptsRaw =
      item?.technician_attempts ?? item?.ticket_obj_id?.technician_attempts ?? [];
    const centerId = item?.center_obj_id?._id;

    let matchedAttempt = null;

    if (Array.isArray(attemptsRaw)) {
      matchedAttempt =
        attemptsRaw.find((entry) => entry?.technician_id === centerId) ||
        attemptsRaw.find((entry) => entry?.attempted);
    } else if (attemptsRaw && typeof attemptsRaw === "object") {
      // When it's already the attempt details object, use it directly.
      matchedAttempt = attemptsRaw;
    }

    const isAttempted = Boolean(matchedAttempt?.attempted);
    const attemptedAt = matchedAttempt?.attemptedAt;

    return {
      isAttempted,
      attemptedAt,
    };
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Center Name</TableCell>
            <TableCell>Last Active</TableCell>
            <TableCell>Attempted</TableCell>
            <TableCell>Center Id</TableCell>
            <TableCell>Wallet Balance</TableCell>
            <TableCell>Center Phone</TableCell>
            <TableCell>Rating</TableCell>
            <TableCell>Subscription</TableCell>
            <TableCell>OTP Verified</TableCell>
            <TableCell>Status of Broadcast</TableCell>
            <TableCell>Closed Request</TableCell>
            {canViewNumbers && <TableCell>Chat</TableCell>}
            {canAddRemark && <TableCell>Remarks</TableCell>}
            {canEditCenter && <TableCell>Edit Center</TableCell>}
            {canRefund && <TableCell>Refund</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {broadCastList.map(
            (item, index) =>
              !item.center_obj_id?.disabled_for?.accepting_broadcast_ticket && (
                <TableRow
                  key={index}
                  sx={{
                    background:
                      item.status_of_ticket === "ACCEPTED" ||
                      item.status_of_ticket === "CLOSED"
                        ? "lightgreen"
                        : item.center_obj_id?.payment_details
                            ?.paid_for_onboarding_kit
                        ? "#22d2f1"
                        : "white",
                  }}
                >
                  <TableCell>{item.center_obj_id?.center_name}</TableCell>
                  <TableCell>
                    {item.center_obj_id?.updatedAt &&
                      new Date(item.center_obj_id.updatedAt).toLocaleDateString(
                        "en-GB"
                      )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const { isAttempted, attemptedAt } = getAttemptDetails(item);
                      if (!isAttempted) return "No";
                      if (!attemptedAt) return "Yes";
                      return `Yes (${new Date(attemptedAt).toLocaleString("en-GB")})`;
                    })()}
                  </TableCell>
                  <TableCell>{item.center_obj_id?.qr_details?.qr_id}</TableCell>
                  <TableCell>
                    {Number(item?.technician_wallet?.balance || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {item.center_obj_id?.personal_details?.phone?.mobile_number
                      ? (() => {
                          const num = String(
                            item.center_obj_id.personal_details.phone
                              .mobile_number
                          );
                          return canViewNumbers
                            ? num
                            : "******" + num.slice(-4);
                        })()
                      : ""}
                  </TableCell>
                  <TableCell>
                    {item?.center_obj_id?.count_details?.average_ticket_rating}
                  </TableCell>
                  <TableCell>
                    {item.center_obj_id?.payment_details
                      ?.paid_for_onboarding_kit
                      ? "Subscribed"
                      : "Not Subscribed"}
                  </TableCell>
                  <TableCell>
                    {item?.center_obj_id?.otp_details?.is_otp_verfied
                      ? "YES"
                      : "NO"}
                  </TableCell>
                  <TableCell>{item?.status_of_ticket}</TableCell>
                  <TableCell>
                    {item?.center_obj_id?.count_details?.closed_ticket_count}
                  </TableCell>
                  {canViewNumbers && (
                    <TableCell>
                      <Box
                        border={2}
                        borderColor="green"
                        borderRadius={2}
                        p={0.001}
                        backgroundColor="transparent"
                      >
                        <IconButton
                          onClick={() =>
                            handleWhatsAppClick(
                              item.center_obj_id?.personal_details?.phone
                                ?.mobile_number,
                              ticketDetails?.address_details?.locality,
                              ticketDetails?.address_details?.city,
                              ticketDetails?.specific_requirement,
                              ticketDetails?.address_details?.pincode
                            )
                          }
                        >
                          <WhatsAppIcon
                            sx={{
                              color: "green",
                              fontSize: 30,
                              padding: "1px",
                            }}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                  {canAddRemark && (
                    <TableCell>
                      <Button
                        onClick={() =>
                          handleOpenRemarkModal(
                            item?.ticket_obj_id?._id,
                            item?.center_obj_id?._id,
                            item?.remarks?.technician_message,
                            item?.remarks?.onboarding_team_name
                          )
                        }
                        variant="outlined"
                        sx={{
                          backgroundColor: item?.remarks?.technician_message
                            ? "green"
                            : "blue",
                          color: "white",
                          "&:hover": {
                            color: "black",
                          },
                        }}
                      >
                        Remark
                      </Button>
                    </TableCell>
                  )}
                  {canEditCenter && (
                    <TableCell>
                      <Button
                        onClick={() =>
                          handleEditCenter(item?.center_obj_id?._id)
                        }
                      >
                        Edit
                      </Button>
                    </TableCell>
                  )}
                  {canRefund && (
                    <TableCell>
                      {(item.status_of_ticket === "ACCEPTED" ||
                        item.status_of_ticket === "CLOSED") && (
                        <Button
                          onClick={() =>
                            handleOpenRefundModal(
                              item?.center_obj_id?._id,
                              item?.ticket_obj_id?._id
                            )
                          }
                          variant="outlined"
                          color="secondary"
                        >
                          Refund
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
          )}
          <TableRow>
            <TableCell
              colSpan={totalColumns}
              sx={{ fontWeight: "bold", fontSize: "16px" }}
            >
              Centers that are disabled for Accepting Requests:
            </TableCell>
          </TableRow>
          {broadCastList.map(
            (item, index) =>
              item.center_obj_id?.disabled_for?.accepting_broadcast_ticket && (
                <TableRow key={index} sx={{ background: "#F14122" }}>
                  <TableCell>{item.center_obj_id?.center_name}</TableCell>
                  <TableCell>
                    {item.center_obj_id?.updatedAt &&
                      new Date(item.center_obj_id.updatedAt).toLocaleDateString(
                        "en-GB"
                      )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const { isAttempted, attemptedAt } = getAttemptDetails(item);
                      if (!isAttempted) return "No";
                      if (!attemptedAt) return "Yes";
                      return `Yes (${new Date(attemptedAt).toLocaleString("en-GB")})`;
                    })()}
                  </TableCell>
                  <TableCell>{item.center_obj_id?.qr_details?.qr_id}</TableCell>
                  <TableCell>
                    {Number(item?.technician_wallet?.balance || 0).toFixed(2)}
                  </TableCell>

                  <TableCell>
                    {item.center_obj_id?.personal_details?.phone?.mobile_number
                      ? (() => {
                          const num = String(
                            item.center_obj_id.personal_details.phone
                              .mobile_number
                          );
                          return canViewNumbers
                            ? num
                            : "******" + num.slice(-4);
                        })()
                      : ""}
                  </TableCell>
                  <TableCell>
                    {item?.center_obj_id?.count_details?.average_ticket_rating}
                  </TableCell>
                  <TableCell>
                    {item.center_obj_id?.payment_details
                      ?.paid_for_onboarding_kit
                      ? "Subscribed"
                      : "Not Subscribed"}
                  </TableCell>
                  <TableCell>
                    {item?.center_obj_id?.otp_details?.is_otp_verfied
                      ? "YES"
                      : "NO"}
                  </TableCell>
                  <TableCell>{item?.status_of_ticket}</TableCell>
                  <TableCell>
                    {item?.center_obj_id?.count_details?.closed_ticket_count}
                  </TableCell>
                  {canViewNumbers && (
                    <TableCell>
                      <Box
                        border={2}
                        borderColor="green"
                        borderRadius={2}
                        p={0.001}
                        backgroundColor="transparent"
                      >
                        <IconButton
                          onClick={() =>
                            handleWhatsAppClick(
                              item.center_obj_id?.personal_details?.phone
                                ?.mobile_number,
                              ticketDetails?.address_details?.locality,
                              ticketDetails?.address_details?.city,
                              ticketDetails?.specific_requirement,
                              ticketDetails?.address_details?.pincode
                            )
                          }
                        >
                          <WhatsAppIcon
                            sx={{
                              color: "green",
                              fontSize: 30,
                              padding: "1px",
                            }}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                  {canAddRemark && (
                    <TableCell>
                      <Button
                        onClick={() =>
                          handleOpenRemarkModal(
                            item?.ticket_obj_id?._id,
                            item?.center_obj_id?._id,
                            item?.remarks?.technician_message,
                            item?.remarks?.onboarding_team_name
                          )
                        }
                        variant="outlined"
                        sx={{
                          backgroundColor: item?.remarks?.technician_message
                            ? "green"
                            : "blue",
                          color: "white",
                          "&:hover": {
                            color: "black",
                          },
                        }}
                      >
                        Remark
                      </Button>
                    </TableCell>
                  )}
                  {canEditCenter && (
                    <TableCell>
                      <Button
                        onClick={() =>
                          handleEditCenter(item?.center_obj_id?._id)
                        }
                      >
                        Edit
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              )
          )}
          {broadCastList?.length === 0 && (
            <TableRow>
              <TableCell colSpan={totalColumns}>No broadcast given</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BroadcastList;
